import { findSkillCatalogEntry } from '@sorrem/shared';
import { useId } from 'react';
import { toSvgIdPrefix } from './skill-tree-ids';
import { resolveSkillGroupTint, SKILL_TREE_CORE_FILL } from './skill-tree-palette';
import { SkillTreeEmblemGlowFilter, SkillTreeLineEmblem } from './SkillTreeLineEmblem';
import { SKILL_TREE_SKILL_EMBLEMS } from './skill-tree-skill-emblems';

/** Half the icon's view box: the emblems' +/-18-unit box plus room for their tallest strokes and glow. */
const HALF_VIEW = 27;

/** Whether skill `skillId` has an emblem for `SkillTreeSkillIcon` to draw. */
export function hasSkillTreeSkillIcon(skillId: string): boolean {
    return SKILL_TREE_SKILL_EMBLEMS[skillId] !== undefined && findSkillCatalogEntry(skillId) !== undefined;
}

interface SkillTreeSkillIconProps {
    readonly skillId: string;
    readonly className?: string;
}

/**
 * A skill's tree emblem on its own, tinted with its group's color, e.g. for
 * its action-bar frame. Null for a skill with no emblem (crafting skills,
 * until crafting awakens).
 */
export function SkillTreeSkillIcon({ skillId, className }: SkillTreeSkillIconProps): JSX.Element | null {
    const glowId = `${toSvgIdPrefix(useId(), 'skill-icon')}-glow`;
    const emblem = SKILL_TREE_SKILL_EMBLEMS[skillId];
    const entry = findSkillCatalogEntry(skillId);
    if (emblem === undefined || entry === undefined) {
        return null;
    }

    return (
        <svg className={className} viewBox={`${-HALF_VIEW} ${-HALF_VIEW} ${HALF_VIEW * 2} ${HALF_VIEW * 2}`} aria-hidden="true">
            <defs>
                {/* The icon's own emblem glow, as the tree scene may not be mounted. */}
                <SkillTreeEmblemGlowFilter id={glowId} />
            </defs>
            <SkillTreeLineEmblem
                layers={emblem}
                color={resolveSkillGroupTint(entry.group)}
                fill={SKILL_TREE_CORE_FILL[entry.branch]}
                scale={1}
                glowFilterId={glowId}
            />
        </svg>
    );
}
